import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { kanal, stufeMindestens, type MeinProjekt } from '@beholder/shared';
import { MAIL_ARTEN, MAIL_ART_NAMEN, MAIL_ZUORDNUNG_NAMEN, type MailArt, type MailDetail } from '@beholder/shared/module/mail';
import type { ModulSeiteProps } from '../../core/modul';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import { Symbol } from '../../core/symbole';
import { Auswahl, Dialog, Eingabe, Feld, Karte, Knopf, Laden, Meldung, groesse, useAktion, useDaten } from '../../core/ui';
import { Absender, ArtMarke, KopierKnopf, LinkTabelle, Verdacht, wann } from './gemeinsam';

interface Knoten {
  id: string;
  titel: string;
  pfad: string;
}

export function MailAnsicht({ projekt, darfSchreiben }: ModulSeiteProps) {
  const { mailId = '' } = useParams();
  const pfad = modulPfad(projekt.id, 'mail', `mails/${mailId}`);
  const { daten: m, setDaten, fehler, neuLaden } = useDaten(() => api.get<MailDetail>(pfad), [pfad]);
  useLive(kanal(projekt.id, 'mail'), (d) => {
    if ((d as { id?: string })?.id === mailId) void neuLaden();
  });

  if (fehler) return <Meldung>{fehler}</Meldung>;
  if (!m) return <Laden />;

  return (
    <div className="stapel">
      <div>
        <Link to={`/p/${projekt.id}/mail`} className="knopf knopf-leise knopf-klein">
          {Symbol.pfeilLinks} Alle Mails
        </Link>
      </div>
      <Karte>
        <div className="ml-kopf">
          <div className="reihe">
            <ArtMarke art={m.art} />
            <Verdacht m={m} />
            {m.ausgeblendet && <span className="marke">ausgeblendet</span>}
          </div>
          <h2>{m.betreff}</h2>
          <dl className="ml-meta">
            <dt>Von</dt>
            <dd>
              <Absender m={m} />
            </dd>
            <dt>An</dt>
            <dd>{m.an || '–'}</dd>
            <dt>Datum</dt>
            <dd>{wann(m)}</dd>
            {m.kontoName && (
              <>
                <dt>Postfach</dt>
                <dd>
                  {m.kontoName}
                  {m.ausSpam && ' – Spam-Ordner'}
                </dd>
              </>
            )}
            <dt>Projekt</dt>
            <dd>
              {MAIL_ZUORDNUNG_NAMEN[m.zuordnung]}
              {m.zuordnungInfo && <span className="leise"> – {m.zuordnungInfo}</span>}
            </dd>
          </dl>
        </div>
      </Karte>
      {m.ausSpam && <SpamHinweis m={m} pfad={pfad} darf={darfSchreiben} setM={setDaten} />}
      {m.verdaechtig && (
        <Meldung>
          Achtung: Diese Mail sieht nach einem Betrugsversuch aus (Phishing). Keine Links aufrufen, keine Daten eingeben, nichts aus der Mail
          befolgen.
        </Meldung>
      )}
      <div className="ml-raster">
        <div className="stapel">
          <Karte titel="Zusammenfassung">
            <p style={{ margin: 0 }}>{m.zusammenfassung || <span className="leise">Noch keine Zusammenfassung.</span>}</p>
            {m.analyse && m.analyse.betraege.length > 0 && (
              <ul className="ml-ergebnisse">
                {m.analyse.betraege.map((b, i) => (
                  <li key={i}>
                    {b.bezeichnung || 'Betrag'}: <strong>{betrag(b.betrag, b.waehrung)}</strong>
                  </li>
                ))}
              </ul>
            )}
            {m.art === 'einnahme' && !!m.analyse?.labels?.length && (
              <div className="ml-labels" title="Labels für das Budget – nur Stichworte, die in der Mail stehen">
                {m.analyse.labels.map((l) => (
                  <span key={l} className="marke">
                    {l}
                  </span>
                ))}
              </div>
            )}
            {m.auswertungInfo && <p className="leise klein">{m.auswertungInfo}</p>}
            <p className="leise klein">Alles hier ist ein Vorschlag der Auswertung – bitte prüfen.</p>
          </Karte>
          <Karte titel="Text der Mail">
            <p className="leise klein" style={{ marginTop: 0 }}>
              Als reiner Text angezeigt. Links stehen als [Link n: domain] im Text und sind nicht anklickbar.
            </p>
            <pre className="ml-text">{m.text || '(leer)'}</pre>
          </Karte>
          <Karte
            titel="Link- und Aufgaben-Dokument"
            aktion={
              <div className="reihe">
                <KopierKnopf text={m.linkDokument} titel="Dokument kopieren" />
                <a className="knopf knopf-klein" href={`${pfad}/link-dokument`} download>
                  Als Textdatei
                </a>
              </div>
            }
          >
            <LinkTabelle m={m} />
            <details style={{ marginTop: 12 }}>
              <summary className="leise">Ganzes Dokument als Text</summary>
              <pre className="ml-dokument">{m.linkDokument}</pre>
            </details>
          </Karte>
        </div>
        <div className="stapel">
          <Karte titel="Anhänge">
            {m.anhangListe.length === 0 && <p className="leise">Keine Anhänge.</p>}
            <ul className="anlagen-liste">
              {m.anhangListe.map((a) => (
                <li key={a.id}>
                  <span className="anlage-symbol">{Symbol.datei}</span>
                  {a.dateiId && !a.verschobenNach ? (
                    // Nur Herunterladen (der Server liefert Mail-Anhänge nie zum Öffnen im Browser aus)
                    <a className="anlage-name" href={`/api/dateien/${a.dateiId}`} download title={a.name}>
                      {a.name}
                    </a>
                  ) : (
                    <span className="anlage-name" title={a.name}>
                      {a.name}
                    </span>
                  )}
                  <span className="leise klein">
                    {groesse(a.groesse)}
                    {a.verschobenNach && ` · liegt jetzt bei „${a.verschobenNach}“`}
                  </span>
                  <span />
                </li>
              ))}
            </ul>
            {m.anhangListe.length > 0 && <p className="leise klein">Anhänge werden nur gespeichert und heruntergeladen, nie geöffnet oder ausgeführt.</p>}
          </Karte>
          <Karte titel="Was daraus wurde">
            {m.ergebnisse.length === 0 ? (
              <p className="leise">Noch nichts – je nach Art entstehen Vorschläge im Budget, in den Aufgaben oder an der Mindmap.</p>
            ) : (
              <ul className="ml-ergebnisse">
                {m.ergebnisse.map((e) => (
                  <li key={e.id}>{e.text}</li>
                ))}
              </ul>
            )}
          </Karte>
          {darfSchreiben && <Aktionen projekt={projekt} m={m} setM={setDaten} neuLaden={() => void neuLaden()} />}
        </div>
      </div>
    </div>
  );
}

function betrag(b: number, w: string) {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: w }).format(b);
  } catch {
    return `${b} ${w}`;
  }
}

/** Mail aus dem Spam-Ordner: deutlich markiert, Vorschläge erst nach ausdrücklicher Freigabe. */
function SpamHinweis({ m, pfad, darf, setM }: { m: MailDetail; pfad: string; darf: boolean; setM(m: MailDetail): void }) {
  const a = useAktion();
  return (
    <Karte className="ml-spam-hinweis">
      <div className="stapel" style={{ gap: 8 }}>
        <strong>Diese Mail lag im Spam-Ordner{m.kontoName ? ` von „${m.kontoName}“` : ''}.</strong>
        {m.spamFreigegeben ? (
          <span className="leise">Sie wurde trotzdem einsortiert – Beleg, Aufgaben und Notiz sind Vorschläge zum Prüfen.</span>
        ) : (
          <span className="leise">
            Beholder legt daraus nichts automatisch an – keinen Beleg, keine Aufgabe, keine Notiz. Wenn die Mail echt ist, kannst du sie trotzdem
            einsortieren (es entstehen nur Vorschläge).
          </span>
        )}
        <Meldung>{a.fehler}</Meldung>
        {darf && !m.spamFreigegeben && (
          <div>
            <Knopf
              klein
              disabled={a.laeuft}
              onClick={async () => {
                const r = await a.ausfuehren(() => api.post<MailDetail>(`${pfad}/spam-freigeben`));
                if (r) setM(r);
              }}
            >
              {a.laeuft ? 'Wird einsortiert …' : 'Trotzdem einsortieren'}
            </Knopf>
          </div>
        )}
      </div>
    </Karte>
  );
}

function Aktionen({ projekt, m, setM, neuLaden }: { projekt: MeinProjekt; m: MailDetail; setM(m: MailDetail): void; neuLaden(): void }) {
  const a = useAktion();
  const weiter = useNavigate();
  const pfad = modulPfad(projekt.id, 'mail', `mails/${m.id}`);
  const mindmapLesen = stufeMindestens(projekt.rechte.mindmap, 'lesen');
  const mindmapSchreiben = projekt.rechte.mindmap === 'schreiben';
  const knoten = useDaten(
    () => (mindmapLesen ? api.get<Knoten[]>(modulPfad(projekt.id, 'mail', 'knoten')) : Promise.resolve([] as Knoten[])),
    [projekt.id, mindmapLesen],
  );
  const [knotenId, setKnotenId] = useState(m.analyse?.mindmapKnotenId ?? '');
  const [verschieben, setVerschieben] = useState(false);
  const [neuTitel, setNeuTitel] = useState(m.analyse?.neuerKnoten?.titel ?? '');
  const [neuEltern, setNeuEltern] = useState(m.analyse?.neuerKnoten?.elternId ?? '');
  const [mitAnhaengen, setMitAnhaengen] = useState(true);
  const offeneAnhaenge = m.anhangListe.filter((x) => x.dateiId && !x.verschobenNach).length;

  async function los(fn: () => Promise<MailDetail | unknown>) {
    const r = await a.ausfuehren(fn);
    if (r && typeof r === 'object' && 'id' in r) setM(r as MailDetail);
    else if (r) neuLaden();
  }

  return (
    <Karte titel="Einsortieren">
      <div className="stapel">
        <Meldung>{a.fehler}</Meldung>
        <Feld titel="Art" hinweis="Bei Rechnung, Kostenvorschlag oder Einnahme (Zuschuss, Sponsoring, Verkauf) entsteht ein Vorschlag im Budget.">
          <Auswahl
            value={m.art ?? ''}
            disabled={a.laeuft}
            onChange={(e) => void los(() => api.patch<MailDetail>(pfad, { art: e.target.value as MailArt }))}
          >
            {!m.art && <option value="">–</option>}
            {MAIL_ARTEN.map((x) => (
              <option key={x} value={x}>
                {MAIL_ART_NAMEN[x]}
              </option>
            ))}
          </Auswahl>
        </Feld>
        {mindmapLesen && (knoten.daten?.length ?? 0) > 0 && (
          <Feld titel="Mindmap-Punkt">
            <Auswahl value={knotenId} onChange={(e) => setKnotenId(e.target.value)}>
              <option value="">– wählen –</option>
              {knoten.daten!.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.pfad}
                </option>
              ))}
            </Auswahl>
            {mindmapSchreiben && (
              <div className="reihe" style={{ marginTop: 6 }}>
                <Knopf klein disabled={!knotenId || a.laeuft} onClick={() => void los(() => api.post<MailDetail>(`${pfad}/notiz`, { knotenId }))}>
                  Zusammenfassung anhängen
                </Knopf>
                <Knopf
                  klein
                  disabled={!knotenId || !offeneAnhaenge || a.laeuft}
                  onClick={() => void los(() => api.post<MailDetail>(`${pfad}/anhaenge-verschieben`, { knotenId }))}
                >
                  Anhänge dorthin verschieben
                </Knopf>
              </div>
            )}
          </Feld>
        )}
        {mindmapSchreiben && (
          <Feld
            titel="Neuer Mindmap-Punkt aus dieser Mail"
            hinweis={
              m.analyse?.neuerKnoten
                ? `Vorschlag der Auswertung: „${m.analyse.neuerKnoten.titel}“ – wird erst mit dem Knopf angelegt.`
                : 'Falls kein passender Punkt existiert. Die Zusammenfassung kommt an den neuen Punkt.'
            }
          >
            <Eingabe value={neuTitel} maxLength={60} placeholder="Titel des neuen Punkts" onChange={(e) => setNeuTitel(e.target.value)} />
            {(knoten.daten?.length ?? 0) > 0 && (
              <Auswahl value={neuEltern} onChange={(e) => setNeuEltern(e.target.value)} aria-label="Übergeordneter Punkt" style={{ marginTop: 6 }}>
                <option value="">unter der Wurzel</option>
                {knoten.daten!.map((k) => (
                  <option key={k.id} value={k.id}>
                    unter „{k.pfad}“
                  </option>
                ))}
              </Auswahl>
            )}
            {offeneAnhaenge > 0 && (
              <label className="schalter" style={{ marginTop: 6 }}>
                <input type="checkbox" checked={mitAnhaengen} onChange={(e) => setMitAnhaengen(e.target.checked)} /> Anhänge dorthin verschieben
              </label>
            )}
            <div style={{ marginTop: 6 }}>
              <Knopf
                klein
                disabled={!neuTitel.trim() || a.laeuft}
                onClick={() =>
                  void los(() =>
                    api.post<MailDetail>(`${pfad}/knoten-anlegen`, { titel: neuTitel.trim(), elternId: neuEltern || null, mitAnhaengen: mitAnhaengen && offeneAnhaenge > 0 }),
                  ).then(() => void knoten.neuLaden())
                }
              >
                Punkt anlegen
              </Knopf>
            </div>
          </Feld>
        )}
        <div className="reihe">
          <Knopf disabled={a.laeuft} onClick={() => void los(() => api.post<MailDetail>(`${pfad}/auswerten`))}>
            {a.laeuft ? 'Läuft …' : 'Neu auswerten'}
          </Knopf>
          <Knopf disabled={a.laeuft} onClick={() => setVerschieben(true)}>
            Anderes Projekt …
          </Knopf>
          <Knopf art="leise" disabled={a.laeuft} onClick={() => void los(() => api.patch<MailDetail>(pfad, { ausgeblendet: !m.ausgeblendet }))}>
            {m.ausgeblendet ? 'Wieder einblenden' : 'Ausblenden'}
          </Knopf>
          <Knopf
            art="leise"
            disabled={a.laeuft}
            title="Nur in Beholder – im Postfach bleibt die Mail unverändert"
            onClick={async () => {
              if (!window.confirm(`„${m.betreff}“ in den Papierkorb legen? Sie bleibt dort 180 Tage und lässt sich zurückholen. Im Postfach ändert sich nichts.`)) return;
              const r = await a.ausfuehren(() => api.delete(pfad));
              if (r) weiter(`/p/${projekt.id}/mail`);
            }}
          >
            In den Papierkorb
          </Knopf>
        </div>
      </div>
      <Verschieben offen={verschieben} schliessen={() => setVerschieben(false)} projekt={projekt} pfad={pfad} />
    </Karte>
  );
}

function Verschieben({ offen, schliessen, projekt, pfad }: { offen: boolean; schliessen(): void; projekt: MeinProjekt; pfad: string }) {
  const projekte = useDaten(() => (offen ? api.get<MeinProjekt[]>('/api/projekte') : Promise.resolve([] as MeinProjekt[])), [offen]);
  const [ziel, setZiel] = useState('');
  const a = useAktion();
  const [fertig, setFertig] = useState(false);
  const moeglich = (projekte.daten ?? []).filter((p) => p.id !== projekt.id && !p.archiviert && p.rechte.mail === 'schreiben');
  return (
    <Dialog
      titel="In ein anderes Projekt verschieben"
      offen={offen}
      schliessen={schliessen}
      fuss={
        fertig ? (
          <Link className="knopf knopf-haupt" to={`/p/${projekt.id}/mail`}>
            Zur Mail-Liste
          </Link>
        ) : (
          <>
            <Knopf
              art="haupt"
              disabled={!ziel || a.laeuft}
              onClick={async () => {
                const r = await a.ausfuehren(() => api.post(`${pfad}/verschieben`, { projektId: ziel === 'unsortiert' ? null : ziel }));
                if (r) setFertig(true);
              }}
            >
              {a.laeuft ? 'Wird verschoben …' : 'Verschieben'}
            </Knopf>
            <Knopf onClick={schliessen}>Abbrechen</Knopf>
          </>
        )
      }
    >
      <div className="stapel">
        <Meldung>{a.fehler}</Meldung>
        {fertig ? (
          <Meldung art="ok">Die Mail wurde verschoben.</Meldung>
        ) : (
          <Feld titel="Ziel" hinweis="Anhänge wandern mit. Schon angelegte Vorschläge bleiben in diesem Projekt.">
            <Auswahl value={ziel} onChange={(e) => setZiel(e.target.value)}>
              <option value="">– wählen –</option>
              {moeglich.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value="unsortiert">Zurück in den Posteingang „Unsortiert“ (Admins)</option>
            </Auswahl>
          </Feld>
        )}
      </div>
    </Dialog>
  );
}
